"use client";

/*
  LanguexExcellence — the consolidated "trust hero" for the
  /quality-guarantee page.

  Replaces two earlier sections that lived side-by-side:
    - ServicesExcellence ("Languex is language excellence" headline
      + Trustpilot box + Explore CTA)
    - CustomerReviews ("What our customers say" + carousel + CTA)

  Imran's call (2026-05-25): combine those two into one cohesive
  block, add a Google Reviews box alongside the Trustpilot box,
  and move the whole thing above the pricing-cards section so it
  builds credibility BEFORE the visitor sees the price. Reviews
  *justify* the price; showing them after the cards inverts that
  flow.

  Composition (desktop):

      [Headline + subhead, centered]

      [Trustpilot box]  [Google box]
            (side-by-side, equal width)

      [Testimonial carousel — 3 cards across]

  Mobile collapses to single-column: boxes stack, carousel shows
  one review at a time. No bottom CTA — both boxes are already
  clickable to their respective review pages, and each testimonial
  card carries Trustpilot branding, so a third "Read all reviews"
  button would be redundant noise (Imran, 2026-05-25).

  Google Reviews box has THREE render states driven by the
  GOOGLE_* constants in Checkout/constants.ts (see GOOGLE_STATE
  below):
    - URL + count + rating  → full "Excellent / N reviews" tile
                              matching the Trustpilot box format
    - URL only              → clickable "Read our reviews on
                              Google" tile (no fabricated numbers)
    - none set              → dashed "Coming soon" placeholder

  The visual shell is identical across states, so layout never
  shifts as the constants get populated over time.
*/

import Image from "next/image";
import { memo, useCallback, useEffect, useState } from "react";
import { CircleChevronLeft, CircleChevronRight } from "lucide-react";
import type { KeenSliderInstance } from "keen-slider";

import * as images from "@/components/images/Images";
import { Typography } from "@/components/ui/typography";
import Carousel, { CarouselSlide } from "@/components/ui/carousel";
import { useSliderEdges } from "@/components/ui/carousel/hooks/useCarouselEdges";
import StarRating from "@/components/ui/star-rating";
import { cn } from "@/lib/utils";
import {
  GOOGLE_RATING_DISPLAY,
  GOOGLE_REVIEW_COUNT,
  GOOGLE_REVIEW_URL,
  TRUSTPILOT_RATING_DISPLAY,
  TRUSTPILOT_REVIEW_COUNT,
  TRUSTPILOT_REVIEW_URL,
} from "@/components/screen/start-order/components/Checkout/constants";
import { trustpilotTestimonials } from "@/data/trustpilotTestimonials";

// Mobile peek: perView 1.1 shows the active card plus a ~10%
// sliver of the next one peeking in from the right edge. The
// peek is the swipe affordance — without it, mobile users who
// don't expect a carousel just see one review and assume that's
// all there is. Above 768px, navigation arrows take over the
// affordance role so we drop back to whole-card views.
const sliderOptions = {
  loop: true,
  slides: {
    perView: 1.1,
    spacing: 16,
  },
  breakpoints: {
    "(min-width: 768px)": {
      slides: { perView: 2, spacing: 24 },
    },
    "(min-width: 1024px)": {
      slides: { perView: 3, spacing: 24 },
    },
  },
};

const testimonials = trustpilotTestimonials;

// Three render states for the Google box (mirrors how Trustpilot's
// own embeds behave when partial data is available):
//   - "none"    : no URL set                         → dashed "Coming soon" tile
//   - "linkOnly": URL set, no count/rating yet       → clickable "View on Google" tile
//   - "full"    : URL + count + rating               → matches Trustpilot box presentation
const GOOGLE_STATE: "none" | "linkOnly" | "full" = !GOOGLE_REVIEW_URL
  ? "none"
  : GOOGLE_REVIEW_COUNT && GOOGLE_RATING_DISPLAY
    ? "full"
    : "linkOnly";

/*
  Inline Google "G" mark — uses Google's official 4-color glyph.
  Inlining avoids a new asset file for a tiny icon, and Google's
  brand guidelines explicitly permit the multi-color G for review
  attribution surfaces.
*/
const GoogleGlyph = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 48 48"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
  >
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const LanguexExcellence = ({ className }: React.ComponentProps<"section">) => {
  const [sliderReady, setSliderReady] = useState<KeenSliderInstance | null>(
    null
  );
  const { isBeginning, isEnd, updateEdges } = useSliderEdges(sliderReady);

  useEffect(() => {
    if (!sliderReady) return;
    sliderReady.on("updated", (s) => updateEdges(s));
  }, [sliderReady, updateEdges]);

  const goToPrevSlide = useCallback(() => {
    sliderReady?.prev();
  }, [sliderReady]);

  const goToNextSlide = useCallback(() => {
    sliderReady?.next();
  }, [sliderReady]);

  return (
    <section
      className={cn("section bg-white lg:bg-background-secondary", className)}
      aria-labelledby="languex-excellence-heading"
    >
      <div className="container mx-auto py-12 lg:py-20">
        {/* Headline */}
        <div className="grid grid-cols-1 gap-4 text-center max-w-3xl mx-auto">
          <Typography variant="heading4">
            <h2 id="languex-excellence-heading">
              Languex is language excellence
            </h2>
          </Typography>
          <Typography
            variant="subHeadingSm"
            className="text-gray-700"
            asChild
          >
            <p>
              Highest in-market rating for human translation, backed by{" "}
              <strong>{TRUSTPILOT_REVIEW_COUNT.toLocaleString()}+</strong>{" "}
              verified reviews on Trustpilot — and growing fast on Google.
            </p>
          </Typography>
        </div>

        {/* Trust source pair — Trustpilot + Google, side-by-side on
            desktop, stacked on mobile. Equal-width by design so
            neither source visually dominates. */}
        <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto">
          {/* Trustpilot box — live data */}
          <a
            href={TRUSTPILOT_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Read all ${TRUSTPILOT_REVIEW_COUNT.toLocaleString()} Languex reviews on Trustpilot`}
            className="group flex flex-col justify-center items-center gap-2 bg-white rounded-2xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all p-5 md:p-6 min-h-[200px]"
          >
            <span className="text-lg md:text-xl font-medium">Excellent</span>
            <StarRating size="md" rating={Number(TRUSTPILOT_RATING_DISPLAY)} />
            <span className="text-[13px] text-gray-700">
              Based on{" "}
              <span className="font-semibold underline group-hover:text-blue-500">
                {TRUSTPILOT_REVIEW_COUNT.toLocaleString()} reviews
              </span>
            </span>
            <span className="flex items-center gap-1 mt-1">
              <Image
                alt="Trustpilot"
                src={images.trustpilot}
                width={24}
                height={24}
                loading="lazy"
                className="shrink-0"
              />
              <span className="font-medium">Trustpilot</span>
            </span>
          </a>

          {/* Google box — three render states (none / link-only /
              full). Visual shell stays identical to the Trustpilot
              box so layout never shifts when constants are filled
              in over time. */}
          {GOOGLE_STATE === "full" && (
            <a
              href={GOOGLE_REVIEW_URL!}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Read all Languex reviews on Google"
              className="group flex flex-col justify-center items-center gap-2 bg-white rounded-2xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all p-5 md:p-6 min-h-[200px]"
            >
              <span className="text-lg md:text-xl font-medium">Excellent</span>
              <StarRating size="md" rating={Number(GOOGLE_RATING_DISPLAY)} />
              <span className="text-[13px] text-gray-700">
                Based on{" "}
                <span className="font-semibold underline group-hover:text-blue-500">
                  {GOOGLE_REVIEW_COUNT!.toLocaleString()} reviews
                </span>
              </span>
              <span className="flex items-center gap-1.5 mt-1">
                <GoogleGlyph className="size-5" />
                <span className="font-medium">Google Reviews</span>
              </span>
            </a>
          )}

          {GOOGLE_STATE === "linkOnly" && (
            <a
              href={GOOGLE_REVIEW_URL!}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Read Languex reviews on Google"
              className="group flex flex-col justify-center items-center gap-3 bg-white rounded-2xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all p-5 md:p-6 min-h-[200px]"
            >
              <GoogleGlyph className="size-9" />
              <span className="text-lg md:text-xl font-medium">
                Google Reviews
              </span>
              <span className="text-[13px] text-gray-700 underline group-hover:text-blue-500">
                Read our reviews on Google
              </span>
            </a>
          )}

          {GOOGLE_STATE === "none" && (
            <div
              className="flex flex-col justify-center items-center gap-2 bg-white rounded-2xl border border-dashed border-neutral-300 p-5 md:p-6 min-h-[200px]"
              aria-label="Google Reviews — coming soon"
            >
              <span className="text-lg md:text-xl font-medium text-gray-500">
                Coming soon
              </span>
              <StarRating size="md" rating={0} className="opacity-40" />
              <span className="text-[13px] text-gray-500">
                Verified Google reviews
              </span>
              <span className="flex items-center gap-1.5 mt-1">
                <GoogleGlyph className="size-5" />
                <span className="font-medium text-gray-700">
                  Google Reviews
                </span>
              </span>
            </div>
          )}
        </div>

        {/* Carousel — testimonials below the trust boxes.
            Mobile: 1.1 cards visible (peek hints at swipeability),
                    arrows hidden — swipe is the gesture.
            sm+:    full arrows shown alongside whole-card views. */}
        <div className="mt-10 md:mt-14 relative flex items-center">
          <button
            onClick={goToPrevSlide}
            className="z-10 text-gray-300 mr-2 md:mr-[30] cursor-pointer relative font-normal outline-none hidden sm:inline-flex items-center justify-center select-none touch-manipulation transition-colors text-semantic-text-primary duration-200 shrink-0 disabled:text-semantic-foreground-weak disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full"
            aria-disabled={isBeginning}
            aria-label="Previous review"
            title="Previous review"
          >
            <CircleChevronLeft width={28} height={28} strokeWidth={1} />
          </button>

          <Carousel getSlider={setSliderReady} options={sliderOptions}>
            {testimonials.map((item, index) => (
              <CarouselSlide key={index} className="pt-2.5 !h-auto">
                <div className="bg-white rounded-2xl border border-neutral-100 p-4 lg:p-5 h-full flex flex-col gap-2 items-start justify-start text-xl font-bold">
                  <div className="flex w-full justify-between items-center">
                    <StarRating className="[&>span]:size-[21.5px] [&_svg]:size-[15px]" />
                    <span className="text-gray-800 flex text-[12px] font-normal">
                      {item.date}
                    </span>
                  </div>
                  <div className="flex flex-col font-normal flex-auto w-full">
                    <h3 className="font-medium text-sm mb-2">{item.title}</h3>
                    <p className="text-[12px] mb-2">{item.text}</p>
                    <p className="text-[12px] text-gray-800 mt-auto pt-[20]">
                      {item.name}
                    </p>
                  </div>
                </div>
              </CarouselSlide>
            ))}
          </Carousel>

          <button
            onClick={goToNextSlide}
            className="z-10 text-gray-300 ml-2 md:ml-[30] cursor-pointer relative font-normal outline-none hidden sm:inline-flex items-center justify-center select-none touch-manipulation transition-colors text-semantic-text-primary duration-200 shrink-0 disabled:text-semantic-foreground-weak disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full"
            disabled={isEnd}
            aria-disabled={isEnd}
            aria-label="Next review"
            title="Next review"
          >
            <CircleChevronRight width={28} height={28} strokeWidth={1} />
          </button>
        </div>

        {/*
          2026-05-25 — "Read all reviews on Trustpilot" CTA removed
          (Imran). The two trust-source boxes above already link
          out (the count is an anchor, the logo is an anchor), and
          the testimonial cards each surface Trustpilot branding +
          dates — so a bottom CTA was reinforcing the same link a
          third time. Removing it tightens the section and reduces
          visual noise without hiding any path to verify reviews.
        */}
      </div>
    </section>
  );
};

export default memo(LanguexExcellence);
